import {
  Component,
  inject,
  INJECTOR,
  linkedSignal,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { base64Decode, isDefinedNotEmpty, Store } from '@softline/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  SOFTLINE_CONFIG_WWS_WARENUEBERNAHME_BESTELLUNG_STEPS,
  SOFTLINE_FEATURE_WWS_WARENUEBERNAHME,
  SOFTLINE_FEATURE_WWS_WARENUEBERNAHME_BESTELLUNG,
} from '../../../warenuebernahme.shared';
import { WwsBestellungStore } from '../../../store/bestellung.store';
import {
  BackNavigable,
  BackNavigationService,
  CommandStore,
  handleRequestError,
  HeaderComponent,
  showRequestErrors,
  SOFTLINE_FEATURE_COMMANDS,
  StepHeaderComponent,
} from '@softline/application';
import { map, Observable, Subscription, switchMap } from 'rxjs';
import { KopfdatenComponent } from '../../../components/kopfdaten/kopfdaten.component';
import { Bestellung } from '../../../types/bestellung';
import { BewegungListComponent } from '../../../components/bewegung-list/bewegung-list.component';
import {
  MessageBarStore,
  ModalStore,
  RepeatDirective,
  ScrollIntoViewDirective,
  SOFTLINE_FEATURE_MESSAGE_BAR,
  SOFTLINE_FEATURE_MODAL,
} from '@softline/ui-core';
import { BestellungUebernehmenDialogComponent } from '../../../dialogs/bestellung-uebernehmen/bestellung-uebernehmen-dialog.component';
import { WwsWarenuebernahmeStore } from '../../../store/warenuebernahme.store';
import { toSignal } from '@angular/core/rxjs-interop';
import { Bewe, Bewelg } from '../../../types/bewegung';
import { PalletItemService } from '../../../services/pallet-item-service';
import { PalletItemRepository } from '../../../repository/pallet-item.repository';
import {
  PageComponent,
  PageHeaderComponent,
  Panel,
  PanelHeaderComponent,
} from '@softapps/core';

@Component({
  selector: 'soft-bestellung-detail',
  imports: [
    CommonModule,
    HeaderComponent,
    StepHeaderComponent,
    KopfdatenComponent,
    BewegungListComponent,
    PageComponent,
    PageHeaderComponent,
    Panel,
    PanelHeaderComponent,
    ScrollIntoViewDirective,
    RepeatDirective,
  ],
  templateUrl: './bestellung-detail.component.html',
  styleUrls: ['./bestellung-detail.component.css'],
  providers: [PalletItemService, PalletItemRepository],
})
export class BestellungDetailComponent implements OnInit, OnDestroy, BackNavigable {
  readonly steps = SOFTLINE_CONFIG_WWS_WARENUEBERNAHME_BESTELLUNG_STEPS;

  readonly injector = inject(INJECTOR);

  private readonly routeParams$: Observable<{ ids: number[]; bestellnummern: string[] }> =
    this.activatedRoute.paramMap.pipe(
      map((params) => params.get('data') ?? ''),
      map((base64Data) => JSON.parse(base64Decode(base64Data)) ?? '{}')
    );

  private readonly bestellungData = toSignal(this.routeParams$);

  private readonly bestellungId$ = this.routeParams$.pipe(map((o) => o[0]));

  readonly bestellungId = toSignal(this.bestellungId$, { initialValue: null });

  readonly bestellung$ = this.routeParams$.pipe(
    switchMap((bestellungData) =>
      this.store
        .observe(
          SOFTLINE_FEATURE_WWS_WARENUEBERNAHME_BESTELLUNG,
          WwsBestellungStore.getters.entity,
          bestellungData.ids[0]
        )
        .pipe(
          map((bestellung) => ({
            ...bestellung,
            bestellnummer: bestellungData.bestellnummern
              .filter((o) => !!o && o !== '')
              .reduce((a, b) => `${a === '' ? '' : `${a},`} ${b}`, ''),
          }))
        )
    )
  );

  readonly bewegungen$ = this.store.observe(
    SOFTLINE_FEATURE_WWS_WARENUEBERNAHME_BESTELLUNG,
    WwsBestellungStore.getters.bewegungen
  );

  readonly #bewegungen = toSignal(this.bewegungen$, { initialValue: [] });
  readonly bewegungen = linkedSignal(() =>
    this.#bewegungen().map((bewegung) => ({
      id: bewegung.id,
      menge: bewegung.artikelMenge.menge,
      einheitId: bewegung.artikelMenge.einheit.id,
      bewelgs: bewegung?.bewelgs ?? [],
    }))
  );

  private bestellungSubscription?: Subscription;
  private bestellung?: Bestellung;
  public loadingBewes = signal(false);

  constructor(
    private store: Store,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private backNavigationService: BackNavigationService
  ) {}

  async ngOnInit(): Promise<void> {
    this.backNavigationService.set(this);

    this.bestellungSubscription = this.bestellung$.subscribe((bestellung) => {
      this.bestellung = bestellung;
    });

    this.store.commit(SOFTLINE_FEATURE_COMMANDS, CommandStore.mutations.addSet, {
      name: BestellungDetailComponent,
      commands: [
        {
          name: '#WWS_WARENUEBERNAHME.BESTELLUNG.LABEL_BESTELLUNG_UEBERNEHMEN',
          class: 'menu bottom-menu primary',
          execute: async () => await this.bestellungUebernehmen(this.bestellung ?? null),
        },
      ],
    });

    try {
      const bestellungData = this.bestellungData();

      if (!bestellungData?.ids) return;

      this.loadingBewes.set(true);

      await this.store.dispatch(
        SOFTLINE_FEATURE_WWS_WARENUEBERNAHME_BESTELLUNG,
        WwsBestellungStore.actions.loadOnce,
        {
          id: bestellungData.ids[0],
        }
      );

      await this.store.dispatch(
        SOFTLINE_FEATURE_WWS_WARENUEBERNAHME_BESTELLUNG,
        WwsBestellungStore.actions.loadBewegungen,
        bestellungData.ids
      );

      this.loadingBewes.set(false);
    } catch (e) {
      showRequestErrors(this.store, e);
    } finally {
      this.loadingBewes.set(false);
    }
  }

  ngOnDestroy(): void {
    if (this.bestellungSubscription) this.bestellungSubscription.unsubscribe();

    this.store.commit(
      SOFTLINE_FEATURE_COMMANDS,
      CommandStore.mutations.removeSet,
      BestellungDetailComponent
    );

    this.store.commit(
      SOFTLINE_FEATURE_WWS_WARENUEBERNAHME_BESTELLUNG,
      WwsBestellungStore.mutations.setBewegungen,
      []
    );

    this.backNavigationService.set(undefined);
  }

  async navigateBack(): Promise<void> {
    await this.router.navigate(['/warenuebernahme/bestellung/schritt-2'], {
      queryParams: {
        idlieferant: this.bestellung?.lieferant.id,
      },
    });
  }

  async onQuantityChange(data: { bewe: Bewe; menge: number }): Promise<void> {
    const updatedBewegungen = [...this.bewegungen()];
    const index = updatedBewegungen.findIndex((o) => o.id === data.bewe.id);
    if (index < 0) return;
    updatedBewegungen[index].menge = data.menge;
    this.bewegungen.set(updatedBewegungen);
  }

  async onLademittelQuantityChange(data: {
    bewe: Bewe;
    bewelg: Bewelg;
    menge: number;
  }): Promise<void> {
    const updatedBewegungen = [...this.bewegungen()];
    const index = updatedBewegungen.findIndex((o) => o.id === data.bewe.id);
    if (index < 0) return;
    const beweData = updatedBewegungen[index];
    const updatedBewelgs = [...beweData.bewelgs];
    const beweLgIndex = updatedBewelgs.findIndex((o) => o.id === data.bewelg.id);
    if (beweLgIndex < 0) return;
    updatedBewelgs[beweLgIndex].liefmenge = data.menge;
    updatedBewelgs[beweLgIndex].liefmenge_offen = data.menge;
    updatedBewegungen[index] = {
      ...updatedBewegungen[index],
      bewelgs: updatedBewelgs,
    };
    this.bewegungen.set(updatedBewegungen);

    console.log('bewegungen updated', updatedBewegungen);
  }

  async bestellungUebernehmen(bestellung: Bestellung | null) {
    console.log('bestellungUebernehmen 1', bestellung);

    const result = await this.store.dispatch(
      SOFTLINE_FEATURE_MODAL,
      ModalStore.actions.open<string, object>(),
      {
        id: 'SOFTLINE_DIALOG_WARENUEBERNAHME_BESTELLUNG_UEBERNEHMEN',
        component: BestellungUebernehmenDialogComponent,
        data: {
          bestellung: bestellung,
        },
        dismiss: true,
      }
    );

    if (result === 'DISMISSED') return;

    console.log('bestellungUebernehmen res', result);

    if (isDefinedNotEmpty(result) && bestellung?.id) {
      try {
        const warenuebernahme = await this.store.dispatch(
          SOFTLINE_FEATURE_WWS_WARENUEBERNAHME_BESTELLUNG,
          WwsBestellungStore.actions.bestellungUebernehmen,
          {
            idbestellungList: this.bestellungData()?.ids ?? [],
            lieferscheinnummer: result,
            bewegungen: this.bewegungen(),
          }
        );

        this.store.commit(
          SOFTLINE_FEATURE_WWS_WARENUEBERNAHME,
          WwsWarenuebernahmeStore.mutations.addOrUpdate,
          warenuebernahme
        );

        if (warenuebernahme) {
          await this.store.dispatch(
            SOFTLINE_FEATURE_MESSAGE_BAR,
            MessageBarStore.actions.success,
            {
              title:
                '#WWS_WARENUEBERNAHME.BESTELLUNG.MESSAGES.TITEL_BESTELLUNG_ABGESCHLOSSEN',
              message:
                '#WWS_WARENUEBERNAHME.BESTELLUNG.MESSAGES.MESSAGE_BESTELLUNG_ABGESCHLOSSEN',
            }
          );

          await this.router.navigate(['/warenuebernahme/detail', warenuebernahme.id]);
        }
      } catch (e) {
        handleRequestError(e, this.injector, { show: true });
      }
    }
  }
}
